import { useEffect, useRef, useState } from "react";
import { FileUp, RefreshCw, Sparkles, Trash2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { http, errMsg } from "../../lib/api";
import { Card } from "../kit/Kit";
import { AIProgress } from "../kit/AIProgress";
import { PreviewBtn, canPreview } from "../kit/FilePreview";
import { can, useAuth } from "../../lib/auth";

const CATS = {
  PM: [["ekatena", "Ekatena", "Reporte y datos fiscales"], ["ciec", "SAT", "CFDI (ZIP/XML), constancia, 32-D y declaraciones"],
    ["eeff", "Estados financieros", "PDF o Excel"], ["balanza", "Balanza de comprobación", "Excel o PDF"],
    ["edocta", "Estados de cuenta", "PDF, CSV o Excel"], ["legal", "Acta constitutiva y poderes", "PDF"]],
  PFAE: [["identificacion", "INE y CURP", "Identificación oficial en PDF"], ["ciec", "SAT", "Constancia, 32-D, declaraciones anuales de persona física y CFDI"],
    ["edocta", "Estados de cuenta", "Personales y del negocio"]],
};
const TYPE_CAT = { estados_financieros: "eeff", balanza: "balanza", ekatena_reporte: "ekatena", ekatena_xlsx: "ekatena", constancia_situacion_fiscal: "ciec",
  opinion_cumplimiento: "ciec", declaracion_anual: "ciec", cfdi: "ciec", estado_cuenta: "edocta", identificacion: "identificacion", acta_constitutiva: "legal" };
export const DOC_LABEL = { estados_financieros: "Estados financieros", balanza: "Balanza", ekatena_reporte: "Reporte Ekatena", ekatena_xlsx: "Datos Ekatena",
  constancia_situacion_fiscal: "Constancia fiscal", opinion_cumplimiento: "Opinión 32-D", declaracion_anual: "Declaración anual", estado_cuenta: "Estado de cuenta",
  identificacion: "Identificación", acta_constitutiva: "Acta / poderes", cfdi: "CFDI SAT", otro: "Otro documento" };
const SEV = { alta: "bg-rose-50 text-rose-700 border-rose-200", media: "bg-amber-50 text-amber-800 border-amber-200", baja: "bg-sky-50 text-sky-800 border-sky-200" };
const ACCEPT = ".pdf,.xlsx,.xlsm,.csv,.zip,.xml,.jpg,.jpeg,.png,.heic,.heif,.tif,.tiff,.webp";

export const CheckChips = ({ checks = [], testid }) => checks.map((c) => (
  <span key={c.codigo} title={`${c.descripcion}${c.condicion_precedente ? ` · Condición sugerida: ${c.condicion_precedente}` : ""}`} data-testid={`${testid}-${c.codigo}`}
    className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${SEV[c.severidad]}`}>{c.titulo}</span>));

const DocRow = ({ j, id, ro, reload }) => {
  const { user } = useAuth();
  const ref = useRef(null);
  const busy = ["queued", "running"].includes(j.status);
  const del = async () => {
    if (!window.confirm(`¿Eliminar «${j.filename}»? Se recalcularán la extracción, las alertas y el motor.`)) return;
    try { await http.delete(`/expedientes/${id}/jobs/${j.id}`); toast.success("Documento eliminado"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const replace = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try { await http.post(`/expedientes/${id}/jobs/${j.id}/replace`, fd); toast.success("Nueva versión en proceso con Risk AI Engine"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const retype = async (t) => {
    try { await http.patch(`/expedientes/${id}/jobs/${j.id}/tipo`, { doc_type: t }); toast.success(`Tipo cambiado a ${DOC_LABEL[t]}`); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <li className="rounded-xl border border-[#E2DDD5] bg-white p-3" data-testid={`doc-row-${j.id}`} style={{ animation: "rise .25s ease-out both" }}>
      {busy || j.status === "error" ? <AIProgress job={j} compact testid={`doc-progress-${j.id}`} /> : (
        <div className="flex flex-wrap items-center gap-2">
          <span className="min-w-0 flex-1 truncate text-sm font-medium" title={j.filename}>{j.filename}</span>
          {j.doc_type && <span className="inline-flex items-center gap-1 rounded-full bg-[#111] px-2 py-0.5 text-[11px] font-semibold text-white" data-testid={`doc-type-${j.id}`}><Sparkles size={10} />{DOC_LABEL[j.doc_type] || j.doc_type}</span>}
          {!j.checks?.length && <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700" data-testid={`doc-ok-${j.id}`}><ShieldCheck size={11} />Validado</span>}
        </div>)}
      {j.checks?.length > 0 && <div className="mt-2 flex flex-wrap gap-1" data-testid={`doc-checks-${j.id}`}><CheckChips checks={j.checks} testid={`doc-check-${j.id}`} /></div>}
      {!busy && <div className="mt-2 flex gap-3 text-xs">
        {can(user, "docs.descargar") && canPreview(j.filename) && <PreviewBtn url={`/expedientes/${id}/jobs/${j.id}/file`} filename={j.filename} testid={`doc-preview-${j.id}`} />}
        {!ro && !j.parent_id && can(user, "docs.subir") && <button onClick={() => ref.current.click()} className="inline-flex items-center gap-1 font-semibold text-[#5C5850] hover:text-[#E8643C]" data-testid={`doc-replace-${j.id}`}><RefreshCw size={12} />Reemplazar</button>}
        {!ro && !j.parent_id && can(user, "docs.eliminar") && <button onClick={del} className="inline-flex items-center gap-1 font-semibold text-[#5C5850] hover:text-rose-600" data-testid={`doc-delete-${j.id}`}><Trash2 size={12} />Eliminar</button>}
        {!ro && j.type === "ai_extraction" && j.doc_type === "otro" && can(user, "docs.subir") && <select defaultValue="" onChange={(e) => e.target.value && retype(e.target.value)} data-testid={`doc-cambiar-tipo-${j.id}`}
          className="rounded-full border border-[#E2DDD5] bg-white px-2 py-0.5 font-semibold text-[#5C5850] hover:border-[#E8643C]"><option value="">Cambiar tipo…</option>
          {Object.entries(DOC_LABEL).filter(([k]) => !["otro", "cfdi"].includes(k)).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>}
        <input ref={ref} type="file" accept={ACCEPT} className="hidden" onChange={(e) => replace(e.target.files[0])} data-testid={`doc-replace-input-${j.id}`} /></div>}
    </li>
  );
};

const Drop = ({ cat, label, hint, jobs, id, ro, reload, wide }) => {
  const { user } = useAuth();
  const canUp = can(user, "docs.subir");
  const up = async (files) => {
    if (!files?.length) return;
    const fd = new FormData();
    [...files].forEach((f) => fd.append("files", f));
    fd.append("category", cat);
    try { await http.post(`/expedientes/${id}/files`, fd); toast.success(`${files.length} documento(s) en Risk AI Engine`); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const [over, setOver] = useState(false);
  const dnd = !ro && canUp ? {
    onDragOver: (e) => { e.preventDefault(); setOver(true); },
    onDragLeave: () => setOver(false),
    onDrop: (e) => { e.preventDefault(); setOver(false); up(e.dataTransfer.files); },
  } : {};
  return (
    <div {...dnd} className={`rounded-2xl border p-4 transition-colors ${over ? "border-[#E8643C] bg-[#FDF0EB]" : "border-[#E2DDD5] bg-[#FAF8F5]"} ${wide ? "md:col-span-2" : ""}`} data-testid={`docs-cat-${cat}`}>
      <div className="flex items-start justify-between gap-3">
        <div><p className="font-semibold">{label}</p><p className="text-xs text-[#8C867A]">{hint}</p></div>
        {!ro && canUp && <label className="inline-flex shrink-0 cursor-pointer items-center gap-1.5 rounded-full bg-white px-3 py-1.5 text-xs font-semibold shadow-sm transition-transform duration-200 hover:-translate-y-0.5" data-testid={`docs-upload-${cat}`}>
          <FileUp size={13} className="text-[#E8643C]" />Subir<input type="file" multiple accept={ACCEPT} className="hidden" onChange={(e) => up(e.target.files)} data-testid={`docs-input-${cat}`} /></label>}
      </div>
      <ul className="mt-3 space-y-2">{jobs.map((j) => <DocRow key={j.id} j={j} id={id} ro={ro} reload={reload} />)}</ul>
      {!jobs.length && <p className="mt-3 rounded-xl border border-dashed border-[#E2DDD5] py-4 text-center text-xs text-[#A39E93]">Sin documentos</p>}
    </div>
  );
};

export const StepDocumentos = ({ id, jobs, setJobs, ro, exp, c }) => {
  const { user } = useAuth();
  const tipo = (exp?.case || c)?.sol?.tipo === "PFAE" ? "PFAE" : "PM";
  const reload = () => http.get(`/expedientes/${id}/jobs`).then((r) => setJobs(r.data));
  const docs = jobs.filter((j) => !["memo", "dictamen", "comite"].includes(j.type));
  const active = docs.some((j) => ["queued", "running"].includes(j.status));
  useEffect(() => {
    if (!active) return undefined;
    const t = setInterval(reload, 1500);
    return () => clearInterval(t);
  }, [active]); // eslint-disable-line react-hooks/exhaustive-deps
  const cats = CATS[tipo];
  const catOf = (j) => {
    const k = j.category === "otros" ? TYPE_CAT[j.doc_type] : j.category;
    return cats.some(([c2]) => c2 === k) ? k : j.category === "otros" ? "otros" : k;
  };
  const otros = docs.filter((j) => catOf(j) === "otros" || !cats.some(([k]) => k === catOf(j)));
  return (
    <Card title="Documentos del expediente" testid="step-documentos"
      subtitle={`${tipo === "PFAE" ? "Persona física con actividad empresarial" : "Persona moral"} · cada carga pasa por Risk AI Engine: clasificación, extracción y validación`}>
      {!ro && can(user, "docs.subir") && <div className="mb-5"><Drop cat="otros" label="Carga rápida" hint="Arrastra cualquier documento o ZIP; Risk AI Engine detecta el tipo y lo acomoda" jobs={[]} id={id} ro={ro} reload={reload} /></div>}
      <div className="grid gap-4 md:grid-cols-2">
        {cats.map(([k, l, h]) => <Drop key={k} cat={k} label={l} hint={h} jobs={docs.filter((j) => catOf(j) === k)} id={id} ro={ro} reload={reload} />)}
        {otros.length > 0 && <Drop cat="otros" label="Otros documentos" hint="Clasificados automáticamente" jobs={otros} id={id} ro={ro} reload={reload} wide />}
      </div>
    </Card>
  );
};
